import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function formatDateShort(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
}

/**
 * Shared in-browser viewer for both Planned Absences and Planned Latenesses.
 * Differences between the two reports are tiny enough (endpoint slug, page
 * title, colour palette of the "Yes" cells, download filename fallback) that
 * a single component beats two copies — they always need to look the same so
 * supervisors can read them the same way.
 */
export default function PlannedGridView({
  endpointBase,       // "/reports/planned-absences-grid" or "/reports/planned-latenesses-grid"
  pageEyebrow,        // "PLANNED ABSENCES"
  defaultScopeLabel,  // "Planned Absences" (fallback before data loads)
  filenameFallback,   // "rrdc-planned-absences"
  cellPalette = "red", // "red" | "amber"
  errorLabel = "report",
}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  // Mobile-only: tapping a staff card expands it to show each date + reason.
  // Multiple cards can be open at once for easy side-by-side review.
  const [openCardIds, setOpenCardIds] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`${endpointBase}/data`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to load ${errorLabel}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpointBase, errorLabel]);

  const onDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`${endpointBase}/xlsx`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const disp = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const fname = match ? match[1] : `${filenameFallback}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const scopeLabel = data?.scope_label || defaultScopeLabel;

  // Cell styling palettes — keeps absences (red) and latenesses (amber)
  // visually distinct so a supervisor flipping between tabs doesn't confuse
  // them.
  const yesCellCls = cellPalette === "amber"
    ? "bg-amber-200 text-amber-900 font-bold"
    : "bg-red-200 text-red-900 font-bold";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-touch inline-flex items-center gap-1 px-2 sm:px-3 rounded-lg text-slate-700 hover:bg-slate-100 shrink-0"
              data-testid="planned-grid-back"
            >
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-contain bg-white shadow-sm p-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold truncate">{pageEyebrow}</div>
              <h1 className="font-display text-base sm:text-lg font-bold text-slate-900 truncate">{scopeLabel}</h1>
            </div>
          </div>
          <button
            onClick={onDownload}
            disabled={downloading || loading}
            className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-sm shrink-0"
            data-testid="planned-grid-download"
          >
            <Download className="w-4 h-4" /> {downloading ? "…" : "Excel"}
          </button>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {loading && (
          <div className="text-center text-slate-500 py-12">Loading…</div>
        )}

        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Nothing on file</div>
              <div className="text-sm">No one in {scopeLabel.toLowerCase()} has any entries yet.</div>
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 mb-3 text-xs text-slate-600 flex items-center justify-between gap-2 flex-wrap">
              <span><strong className="text-slate-900">{rows.length}</strong> staff with entries</span>
              <span className="text-slate-500 hidden sm:inline">Hover a coloured cell to see the reason</span>
              <span className="text-slate-500 sm:hidden">Tap any card to see reasons</span>
            </div>

            {/* MOBILE: card per staff with dates listed as pills. The 43-column
                grid below is unusable on a phone (user feedback).
                Each card is fully clickable to expand and show every date +
                reason in a tappable detail list. */}
            <div className="sm:hidden space-y-2">
              {rows.map((r, idx) => {
                const inWindow = Object.entries(r.dates || {})
                  .sort((a, b) => a[0].localeCompare(b[0]));
                const extras = r.extra_dates || [];
                const totalEntries = inWindow.length + extras.length;
                const cardKey = r.name_last_first + "_" + idx;
                const isOpen = !!openCardIds[cardKey];
                return (
                  <div
                    key={cardKey}
                    className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                    data-testid={`planned-card-${idx}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenCardIds(prev => ({ ...prev, [cardKey]: !prev[cardKey] }))}
                      className="w-full text-left p-3 active:bg-slate-50"
                      data-testid={`planned-card-toggle-${idx}`}
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1.5">
                        <div className="font-display font-bold text-slate-900 leading-tight">{r.name_last_first}</div>
                        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-bold shrink-0">
                          {totalEntries} {totalEntries === 1 ? "date" : "dates"} · {isOpen ? "tap to close" : "tap to view"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mb-2">{r.division || "—"}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {inWindow.map(([date]) => (
                          <span
                            key={date}
                            className={`px-2 py-0.5 rounded-md text-xs font-semibold ${yesCellCls}`}
                          >
                            {formatDateShort(date)}
                          </span>
                        ))}
                        {extras.map((e) => (
                          <span
                            key={e.date}
                            className={`px-2 py-0.5 rounded-md text-xs font-semibold border border-dashed ${yesCellCls}`}
                          >
                            {formatDateShort(e.date)}
                          </span>
                        ))}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 space-y-1" data-testid={`planned-card-detail-${idx}`}>
                        {inWindow.length === 0 && extras.length === 0 && (
                          <div className="text-xs text-slate-500">No reasons recorded.</div>
                        )}
                        {inWindow.map(([date, reason]) => (
                          <div key={`in-${date}`} className="flex items-baseline gap-2 text-sm">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${yesCellCls} shrink-0`}>
                              {formatDateShort(date)}
                            </span>
                            <span className="text-slate-700">{reason || <em className="text-slate-400">(no reason given)</em>}</span>
                          </div>
                        ))}
                        {extras.map((e) => (
                          <div key={`ex-${e.date}`} className="flex items-baseline gap-2 text-sm">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold border border-dashed ${yesCellCls} shrink-0`}>
                              {formatDateShort(e.date)}
                            </span>
                            <span className="text-slate-700">{e.reason || <em className="text-slate-400">(no reason given)</em>}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* DESKTOP / TABLET: full sortable grid. Hidden on small screens. */}
            <div className="hidden sm:block rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto max-h-[78vh]">
              <table className="text-xs sm:text-sm tabular-nums" data-testid="planned-grid-table">
                <thead className="bg-slate-900 text-white sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-slate-900 z-20 min-w-[200px] border-r border-slate-700">Name</th>
                    <th className="text-left px-3 py-2 font-semibold sticky left-[200px] bg-slate-900 z-20 min-w-[140px] border-r border-slate-700">Division</th>
                    {columns.map((c) => {
                      const isOrient = c.label === "Orient" || c.label === "MYM";
                      return (
                        <th
                          key={c.date}
                          className={`text-center px-2 py-2 font-semibold whitespace-nowrap min-w-[68px] ${isOrient ? "bg-violet-700" : ""}`}
                          title={c.date}
                        >
                          <div className="text-[10px] uppercase tracking-wide opacity-80">{c.label}</div>
                          <div>{formatDateShort(c.date)}</div>
                        </th>
                      );
                    })}
                    <th className="text-left px-3 py-2 font-semibold whitespace-nowrap min-w-[200px]">
                      <div className="text-[10px] uppercase tracking-wide opacity-80">Off-axis</div>
                      <div>Other Dates</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr
                      key={r.name_last_first + idx}
                      className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}
                      data-testid={`planned-grid-row-${idx}`}
                    >
                      <td className="px-3 py-1.5 font-semibold text-slate-900 sticky left-0 z-10 border-r border-slate-200" style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        {r.name_last_first}
                      </td>
                      <td className="px-3 py-1.5 text-slate-600 sticky left-[200px] z-10 border-r border-slate-200" style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        {r.division || "—"}
                      </td>
                      {columns.map((c) => {
                        const reason = r.dates?.[c.date];
                        const isHit = reason !== undefined && reason !== null;
                        return (
                          <td
                            key={c.date}
                            className={`text-center px-1 py-1.5 ${isHit ? yesCellCls : "text-slate-300"}`}
                            title={isHit ? (reason || "Entry on file") : ""}
                          >
                            {isHit ? "Yes" : "·"}
                          </td>
                        );
                      })}
                      <td className={`px-3 py-1.5 text-xs ${(r.extra_dates || []).length ? yesCellCls : "text-slate-300"}`}>
                        {(r.extra_dates || []).length
                          ? r.extra_dates.map((e) => (
                              <span key={e.date} title={e.reason || "Entry on file"} className="inline-block mr-1.5">
                                {formatDateShort(e.date)}
                                {e.reason ? ` (${e.reason})` : ""}
                              </span>
                            ))
                          : "·"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          </>
        )}

        {!loading && data && (
          <div className="mt-3 text-[11px] text-slate-400">
            Generated {new Date(data.generated_at_utc).toLocaleString()} · Scope: {scopeLabel}
          </div>
        )}
      </main>
    </div>
  );
}
